import { claimsLevel, LEVEL_IDENTITY, validLevelNumber } from "./coachCopy";

/**
 * The six pace groups a squad can land in. KEYS ONLY (D12).
 *
 * Each entry carried a `label` as well, and not one of the six was rendered
 * anywhere: both consumers here read `key`, and every surface that states a
 * position gets its words from `statusVocabulary` (until FE-9.9a removed it,
 * the pace bar's `PACE_POSITIONS` view did too, via `statusTerm`). They were
 * the THIRD naming of the pace axis in this tree,
 * they were the retired spelling of it ("work turned in", "behind expected
 * pace"), and the next reader would reasonably have assumed they were live.
 */
export const ROOM_GROUPS = [
  { key: "turned_in" },
  { key: "not_evidenced" },
  { key: "pace_missing" },
  { key: "behind" },
  { key: "at" },
  { key: "ahead" },
];

const emptyGroups = () =>
  Object.fromEntries(ROOM_GROUPS.map(({ key }) => [key, []]));

export const validateCapsuleTopology = (capsules) => {
  const ordered = Array.isArray(capsules) ? capsules : [];
  const ids = new Set();
  const levels = new Set();
  const normalized = [];

  for (const capsule of ordered) {
    const id = validLevelNumber(capsule?.id);
    const levelNumber = validLevelNumber(capsule?.level_number);
    if (
      id === null ||
      levelNumber === null ||
      ids.has(id) ||
      levels.has(levelNumber)
    ) {
      return { valid: false, capsules: [], signature: "invalid" };
    }
    ids.add(id);
    levels.add(levelNumber);
    normalized.push({
      id,
      levelNumber,
      title: String(capsule?.title || `level ${levelNumber}`),
    });
  }

  return {
    valid: normalized.length > 0,
    capsules: normalized,
    signature: normalized
      .map(({ id, levelNumber }) => `${id}:${levelNumber}`)
      .join("|"),
  };
};

/**
 * Terminal, and it is EXPORTED so the stage reading and the matrix column can
 * use the same predicate the classifier does. A squad whose activity_state is
 * `closed` while its artifact_state still says `executing` is terminal here;
 * a second, hand-rolled "is it turned in" test elsewhere would put that squad
 * in the executing column while the pace counts called it turned in, and the
 * two readings on one screen would disagree for a reason nobody can see.
 */
export const isTerminal = (squad) =>
  squad?.artifact_state === "submitted" ||
  squad?.artifact_state === "graded" ||
  squad?.activity_state === "closed" ||
  squad?.check_ins?.level_identity === LEVEL_IDENTITY.AWAITING_NEXT;

export const classifyRoom = (squads, capsules, expectedLevel) => {
  const source = Array.isArray(squads) ? squads : [];
  const topology = validateCapsuleTopology(capsules);
  const groups = emptyGroups();
  const byLevel = new Map(
    topology.capsules.map((capsule) => [capsule.levelNumber, []]),
  );
  const topologyById = new Map(
    topology.capsules.map((capsule) => [capsule.id, capsule]),
  );
  const expected = validLevelNumber(expectedLevel);

  // PLACEMENT: an ADDITIVE second reading of the same pass, and its shape is
  // the whole safety argument.
  //
  // `byLevel` above cannot answer "which rung is this squad on" for a terminal
  // squad, because the terminal branch below returns before `byLevel` is ever
  // written. That is correct for a PACE reading (work that is in has stopped
  // being compared to a lens) and wrong for a LOCATION reading, which is what
  // the matrix renders. So placement is computed separately, for every squad,
  // and `groups`, `counts`, `byLevel`, `total` and `accountedFor` are left
  // exactly as they were. The existing `byLevel` key is NOT repurposed:
  // repurposing it is how a "harmless widening" reaches a consumer nobody
  // enumerated. FE-9.9b is that demand being honoured rather than argued with:
  // the level spine wanted the location reading, so it was REPOINTED here
  // rather than `byLevel` being widened under it. `byLevel` now has no UI
  // consumer and stays byte-pinned by `RoomMatrix.test.jsx`.
  const placementByLevel = new Map(
    topology.capsules.map((capsule) => [capsule.levelNumber, []]),
  );
  const unplaceable = [];

  source.forEach((squad) => {
    const identity = squad?.check_ins?.level_identity;
    const capsuleId = validLevelNumber(
      squad?.check_ins?.evaluation_capsule?.id,
    );
    const squadLevel = validLevelNumber(
      squad?.check_ins?.evaluation_capsule?.level_number,
    );
    const topologyCapsule = topologyById.get(capsuleId);

    // The SAME three gates the evidenced test applies, with
    // `identity === EVIDENCED` widened to `claimsLevel(identity)`. That widens
    // it by exactly one value, `awaiting_next`, whose capsule is the level the
    // squad finished and most recently submitted: a real, evidenced location.
    // `presumed` and `unavailable` still go to the detached strip, because
    // under `presumed` the backend returns the program's FIRST capsule and
    // placing that squad on rung 1 would be a guess wearing the styling of a
    // fact.
    if (
      topology.valid &&
      claimsLevel(identity) &&
      topologyCapsule?.levelNumber === squadLevel
    ) {
      placementByLevel.get(squadLevel).push(squad);
    } else {
      unplaceable.push(squad);
    }

    if (isTerminal(squad)) {
      groups.turned_in.push(squad);
      return;
    }

    const evidenced =
      topology.valid &&
      identity === LEVEL_IDENTITY.EVIDENCED &&
      topologyCapsule?.levelNumber === squadLevel;

    if (!evidenced) {
      groups.not_evidenced.push(squad);
      return;
    }

    byLevel.get(squadLevel).push(squad);
    if (expected === null) groups.pace_missing.push(squad);
    else if (squadLevel < expected) groups.behind.push(squad);
    else if (squadLevel > expected) groups.ahead.push(squad);
    else groups.at.push(squad);
  });

  const counts = Object.fromEntries(
    ROOM_GROUPS.map(({ key }) => [key, groups[key].length]),
  );

  return {
    total: source.length,
    topology,
    groups,
    counts,
    byLevel,
    /* THE RESOLVED LENS, RETURNED RATHER THAN DISCARDED (FE-9.9a, codex
       BLOCKER 1). It is `null` when no valid lens is recorded, and that
       condition is NOT the same proposition as `counts.pace_missing > 0`:
       `pace_missing` is written only inside the evidenced, non-terminal branch
       below, so a room with no lens and no evidenced non-terminal squads (every
       squad turned in, say, which is the exact room FE-9.9b's acceptance shot
       uses) reports zero for it while still having no lens at all. The room
       reading's pace sentence has to tell those two apart to say "no pace
       recorded" in the room where it matters most, and reading a field the
       classifier already computed is the same doctrine the counts follow:
       stated once, read, never re-derived by a consumer. */
    expected,
    placement: { byLevel: placementByLevel, unplaceable },
    accountedFor:
      groups.turned_in.length +
      groups.not_evidenced.length +
      groups.pace_missing.length +
      groups.behind.length +
      groups.at.length +
      groups.ahead.length,
  };
};

export const pacePreviewSignature = (classification, pace) =>
  [
    classification?.topology?.signature || "invalid",
    classification?.counts?.behind ?? 0,
    classification?.counts?.at ?? 0,
    classification?.counts?.ahead ?? 0,
    classification?.counts?.not_evidenced ?? 0,
    classification?.counts?.turned_in ?? 0,
    pace?.storedLevel ?? "none",
    pace?.changedAt ?? "none",
    pace?.source ?? "none",
  ].join("|");
